import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { readString } from 'react-papaparse';

import './Form.css';

import { Loader } from '../Loader/Loader';
import { Error } from '../Error/Error';

import Button from '@mui/material/Button';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import {
  formatText,
  convertCsvDataToJson,
  findUniqueValues,
} from '../../Utils/services';

import csvFile from '../../Data/data_v001.csv';
import ENGLISH from '../../ENGLISH.jpg';
import SPANISH from '../../SPANISH.jpg';

const Form = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [language, setLanguage] = useState('');
  const [languages, setLanguages] = useState(null);
  const [topic, setTopic] = useState('');
  const [topics, setTopics] = useState(null);
  const [query, setQuery] = useState('');
  const [queries, setQueries] = useState(null);
  const [stance, setStance] = useState('');
  const [stances, setStances] = useState(null);
  const [initialData, setInitialData] = useState(null);

  useEffect(() => {
    readString(csvFile, {
      download: true,
      complete: (results) => {
        const data = convertCsvDataToJson(results);

        setInitialData(data);

        const uniqueValues = findUniqueValues('language', data);
        const languages = uniqueValues.map((value, i) => {
          return (
            <ToggleButton key={'lang' + i} value={value}>
              <img
                className='toggle-image'
                src={value === 'SPANISH' ? SPANISH : ENGLISH}
                alt={value}
              />
            </ToggleButton>
          );
        });
        setLanguages(languages);
        setTimeout(() => {
          setLoading(false);
          setError(null);
        }, 1000);
      },
      error: (error, file) => {
        console.log('ERROR PARSING CSV', error, file);
        setTimeout(() => {
          setLoading(false);
          setError('Error while parsing the csv file');
        }, 1000);
      },
    });
  }, []);

  const handleLanguageChange = (event, value) => {
    setLanguage(value);

    const filteredData = initialData.filter((item) => {
      return item.language === value;
    });
    console.log('FILTERED DATA BY LANGUAGE', filteredData);

    const uniqueValues = findUniqueValues('topic', filteredData);
    const topics = uniqueValues.map((value, i) => {
      return (
        <ToggleButton key={'topic' + i} value={value}>
          {formatText(value, false)}
        </ToggleButton>
      );
    });
    setTopics(topics);

    setTopic('');
    setQuery('');
    setStance('');
    setActiveStep(1);
  };

  const handleTopicChange = (event, value) => {
    setTopic(value);

    const filteredData = initialData.filter((item) => {
      return item.language === language && item.topic === value;
    });
    console.log('FILTERED DATA BY LANGUAGE & TOPIC', filteredData);

    const uniqueValues = findUniqueValues('query', filteredData);
    const queries = uniqueValues.map((value, i) => {
      return (
        <MenuItem key={'query' + i} value={value}>
          {value}
        </MenuItem>
      );
    });
    setQueries(queries);

    setQuery('');
    setStance('');
    setActiveStep(2);
  };

  const handleQueryChange = (event) => {
    setQuery(event.target.value);

    const filteredData = initialData.filter((item) => {
      return (
        item.language === language &&
        item.topic === topic &&
        item.query === event.target.value
      );
    });
    console.log('FILTERED DATA BY LANGUAGE, TOPIC, & QUERY', filteredData);

    let stances = null;
    if (filteredData.length) {
      let uniqueValues = ['No opinion'];
      uniqueValues.push(filteredData[0]['stance_1_text']);
      uniqueValues.push(filteredData[0]['stance_2_text']);
      uniqueValues.push(filteredData[0]['stance_3_text']);
      console.log('STANCE VALUES', uniqueValues);
      stances = uniqueValues.map((value, i) => {
        return (
          <MenuItem key={'stance' + i} value={i}>
            {value}
          </MenuItem>
        );
      });
    }
    setStances(stances);

    setStance('');
    setActiveStep(3);
  };

  const handleStanceChange = (event) => {
    setStance(event.target.value);

    setActiveStep(4);
  };

  const navigate = useNavigate();

  const goToResult = () => {
    navigate({
      pathname: '/result',
      search: `?language=${language}&topic=${topic}&query=${query}&stance=${stance}`,
    });
  };

  return (
    <div className='form-container'>
      {loading ? (
        <Loader />
      ) : error ? (
        <Error error={error} />
      ) : (
        <div className='form-steps'>
          {activeStep >= 0 && (
            <div className='form-group'>
              <div className={'step first ' + (activeStep < 1 && 'active')}>
                <div className='number'>1</div>
              </div>
              <div className='form-label'>Select a language</div>
              <ToggleButtonGroup
                exclusive
                onChange={handleLanguageChange}
                value={language}
              >
                {languages}
              </ToggleButtonGroup>
            </div>
          )}
          {activeStep >= 1 && (
            <div className='form-group'>
              <div className={'step ' + (activeStep < 2 && 'active')}>
                <div className='number'>2</div>
              </div>
              <div className='form-label'>Select a topic</div>
              <ToggleButtonGroup
                exclusive
                onChange={handleTopicChange}
                value={topic}
              >
                {topics}
              </ToggleButtonGroup>
            </div>
          )}
          {activeStep >= 2 && (
            <div className='form-group'>
              <div className={'step ' + (activeStep < 3 && 'active')}>
                <div className='number'>3</div>
              </div>
              <div className='form-label'>Select a query</div>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel id='query-select-label'>Query</InputLabel>
                <Select
                  labelId='query-select-label'
                  id='query-select'
                  value={query}
                  label='Query'
                  onChange={handleQueryChange}
                >
                  {queries}
                </Select>
              </FormControl>
            </div>
          )}
          {activeStep >= 3 && (
            <div className='form-group'>
              <div className={'step ' + (activeStep < 4 && 'active')}>
                <div className='number'>4</div>
              </div>
              <div className='form-label'>Select a stance</div>
              <FormControl sx={{ minWidth: 160 }}>
                <InputLabel id='stance-select-label'>Stance</InputLabel>
                <Select
                  labelId='stance-select-label'
                  id='stance-select'
                  value={stance}
                  label='Stance'
                  onChange={handleStanceChange}
                >
                  {stances}
                </Select>
              </FormControl>
              {stance !== '' &&
                (stance === 0 ? (
                  <div className='info'>
                    * We will check which news articles support or criticise a
                    general definition of the topic you are asking for.
                  </div>
                ) : (
                  <div className='info'>
                    * We will check which news articles supports or contradicts
                    your point of view.
                  </div>
                ))}
            </div>
          )}
          {activeStep >= 4 && (
            <div className='button-container'>
              <Button variant='contained' onClick={() => goToResult()}>
                Search
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Form;
